import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { verifyEmail, signOutUser } from "../../lib/firebase/firebase";
import { verificationMessage, verificationSecondsRemaining } from "../../lib/firebase/emailVerification";
import { createCheckoutSession } from "../../lib/stripe/createCheckoutSession";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getUTMForAnalytics } from "../../lib/analytics/utmTracking";
import { trackIntercomEvent } from "../../lib/intercom/intercom";
import ReactGA from "react-ga4";
import { isNativeiOS } from "../../lib/utils/platform";
import IOSPaywall from "../../components/PaywallModal/IOSPaywall";
import "../../pages/Auth/SignIn.css";
import LifeTimePro from "./LifeTimePro";

const Checkout = () => {
  const history = useHistory();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(() => verificationSecondsRemaining(user));

  useEffect(() => {
    const update = () => {
      setSecondsRemaining(verificationSecondsRemaining(user));
      const message = verificationMessage(user);
      if (message) setEmailMessage(message);
    };
    setEmailMessage(verificationMessage(user));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [user]);

  const handleUpgrade = async (subscriptionType) => {
    setLoading(true);
    try {
      const utmAnalytics = getUTMForAnalytics();
      ReactGA.event('begin_checkout', {
        currency: 'USD',
        value: subscriptionType === 'designer' ? 7.99 : 0,
        items: [{
          item_id: subscriptionType,
          item_name: subscriptionType === 'designer' ? 'Designer Plan' : 'Lifetime Plan',
          price: subscriptionType === 'designer' ? 7.99 : 0,
          quantity: 1
        }],
        utm_source: utmAnalytics.source,
        utm_medium: utmAnalytics.medium,
        utm_campaign: utmAnalytics.campaign,
        utm_term: utmAnalytics.term,
        utm_content: utmAnalytics.content,
      });
      trackIntercomEvent('viewed_checkout', { plan: subscriptionType });
      await createCheckoutSession(user.uid, subscriptionType);
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setLoading(false);
    }
  };


  const handleResendEmail = async () => {
    setSendingEmail(true);
    try {
      const result = await verifyEmail();
      setEmailMessage(result.message);
    } catch (_) {
      setEmailMessage("We couldn't send the verification email. Please try again later.");
    } finally {
      setSendingEmail(false);
      setSecondsRemaining(verificationSecondsRemaining(user));
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await signOutUser();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      history.replace("/signin");
      return;
    }
    const functions = getFunctions();
    const trackView = httpsCallable(functions, "trackPaywallView");
    trackView().catch((err) => console.warn("Paywall view tracking failed:", err.message));
  }, [user, history]);

  if (!user) return null;

  return (
    <IonPage>
      <IonContent className="auth-background">
        {!user.emailVerified && (
          <div className="auth-wrapper">
            <div className="auth-card">
              <button
                className="auth-logout-btn"
                onClick={handleLogout}
              >
                Sign Out
              </button>

              <div className="auth-heading" style={{ marginTop: 8 }}>
                <h2>Verify your email</h2>
                <p>Please verify your email then refresh the page.</p>
              </div>

              <div className="auth-form">
                <button
                  className="auth-submit-btn"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </button>
                <button
                  className="auth-social-btn"
                  onClick={handleResendEmail}
                  disabled={loading || sendingEmail || secondsRemaining > 0}
                  style={{ justifyContent: 'center' }}
                >
                  {sendingEmail ? <IonSpinner name="crescent" /> : secondsRemaining > 0 ? `Resend in ${secondsRemaining}s` : "Resend Email"}
                </button>
                {emailMessage && <p role="status">{emailMessage}</p>}
              </div>
            </div>
          </div>
        )}
        {isNativeiOS() ? (
          <IOSPaywall
            standalone
            onClose={() => history.replace("/home")}
            onSuccess={() => {
              history.replace("/home");
              window.location.reload();
            }}
          />
        ) : (
          <LifeTimePro />
        )}
      </IonContent>
    </IonPage>
  );
};

export default Checkout;
